'use client';

import 'quill/dist/quill.snow.css'; // Add css for snow theme

import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { QuillOptions } from 'quill';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react';
import { useQuill } from 'react-quilljs';
import { QuillEditorProps, QuillEditorRefs } from './quill-editor';
import './styles.css';

export interface QuillEditorV2Props extends QuillEditorProps {}

/**
 * Chi cac the ma Quill thuc su sinh ra khi soan thao (whitelist).
 */
const KNOWN_HTML_TAG_REGEX =
    /<\/?(p|br|strong|b|em|i|u|s|strike|ol|ul|li|h[1-6]|span|a|div|blockquote|img)(\s[^>]*)?\/?>/gi;

/**
 * `value` truyen vao doi khi la text thuan (VD: nguoi dung go o che do Text
 * roi doi sang Email) chu khong phai HTML that. `dangerouslyPasteHTML` lai
 * parse moi thu nhu HTML, nen cac dau `<`/`>` "lac" (khong thuoc the da biet)
 * - vi du cu phap tu khoa `<<Ten truong>>` - se bi hieu nham thanh the la va
 * bi nuot mat. Escape cac dau `<`/`>` do thanh entity truoc khi paste, chi
 * giu nguyen cac the nam trong whitelist.
 */
function escapeStrayAngleBrackets(value: string): string {
    const placeholders: string[] = [];
    const protectedValue = value.replace(KNOWN_HTML_TAG_REGEX, (m) => {
        placeholders.push(m);
        return '@@PH' + (placeholders.length - 1) + '@@';
    });

    const escaped = protectedValue.replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return escaped.replace(
        /@@PH(\d+)@@/g,
        (_, i) => placeholders[Number(i)]
    );
}

export interface QuillEditorV2Refs extends QuillEditorRefs {}

const quillOptions: QuillOptions = {
    theme: '',
    modules: {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }], // Headers (H1, H2, H3)
            ['bold', 'italic', 'underline', 'strike', 'link'], // Định dạng chữ
            // [{ script: 'sub' }, { script: 'super' }], // Chỉ số trên/dưới
            [{ list: 'ordered' }, { list: 'bullet' }], // Danh sách
            [{ indent: '-1' }, { indent: '+1' }], // Thụt lề
            [{ align: [] }], // Căn chỉnh văn bản
            [{ color: [] }, { background: [] }], // Chọn màu chữ, nền
            // ['link', 'image', 'video'], // Chèn liên kết, hình ảnh, video
            // ['code-block'], // Thêm đoạn mã
            ['clean'], // Xóa định dạng
        ],
    },
};

/**
 * Recommend sử dụng uncontrolled mode
 * @default controlled false
 */
const QuillEditorV2 = forwardRef<QuillEditorV2Refs, QuillEditorV2Props>(
    (props, ref) => {
        const transDef = useTranslations('System');

        const _isInited = useRef(false);

        const {
            controlled = false,
            placeholder = `${transDef('System_Enter')}. . .`,
            wrapperClassName,
            className,
            readOnly,
            onChange,
            value,
        } = props;

        const { quill, quillRef } = useQuill({
            ...quillOptions,
            placeholder: placeholder,
            readOnly: readOnly,
        });

        /**Xử lý tự động focus vào cuối */
        const handleAutoFocusEnd = useCallback((root: HTMLDivElement) => {
            if (root) {
                const range = document.createRange();
                const selection = window.getSelection();
                range.selectNodeContents(root);
                range.collapse(false);
                selection?.removeAllRanges();
                selection?.addRange(range);
            }
        }, []);

        /**Run first */
        useEffect(() => {
            if (
                !_isInited.current &&
                quill &&
                typeof value === 'string' &&
                value
            ) {
                quill.clipboard.dangerouslyPasteHTML(escapeStrayAngleBrackets(value));
                handleAutoFocusEnd(quill.root);
                _isInited.current = true;
            } else {
            }
        }, [quill, value, handleAutoFocusEnd]);

        useEffect(() => {
            if (quill && controlled) {
                quill.on('text-change', (_, __, source) => {
                    if (source === 'silent') return;
                    const html = quill.root.innerHTML;
                    onChange?.(html);
                });
            }
            return () => {
                quill?.off('text-change');
            };
        }, [quill, onChange, controlled]);

        useImperativeHandle(ref, () => {
            return {
                getValue() {
                    return quill?.root.innerHTML ?? '';
                },
                setValue(str) {
                    quill?.clipboard.dangerouslyPasteHTML(str);
                },
            };
        }, [quill?.clipboard, quill?.root.innerHTML]);

        return (
            <div
                onKeyDown={(e) => {
                    e.stopPropagation();
                }}
                id='quill-editor'
                className={cn(
                    'w-full shadow-md [&>.ql-container]:bg-white',
                    '[&>.ql-toolbar]:bg-white',
                    '[&>.ql-container]:bg-white',
                    '[&_.ql-editor]:max-h-[350px] [&_.ql-editor]:min-h-[250px] [&_.ql-editor]:overflow-y-auto',
                    wrapperClassName,
                    className
                )}
                // style={{ height: 250 }}
            >
                <div ref={quillRef} />
            </div>
        );
    }
);

QuillEditorV2.displayName = 'QuillEditorV2';

export default QuillEditorV2;
